import {
	LineChart,
	Line,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
} from "recharts";
import { AlertCircle } from "lucide-react";
import { transformToWideFormat } from "../../utils/chartDataTransformer";
import { useApp } from '../../contexts/AppProvider';

function MultiLineCharts({ chartData }) {
	const { language } = useApp();

	// Helper function to check if data fetch failed
	const hasFailed = (data) => {
		return data && data.has_failed === true;
	};

	const chartConfigs = [
		{
			title: language === "en" ? "Completed per day per token" : "Nombre de réponse validée par jour et par tokens",
			data: hasFailed(chartData.completed_per_day_per_token_name)
				? null
				: transformToWideFormat(
					chartData.completed_per_day_per_token_name,
					"token_name",
					"completed_responses"
				),
			failedData: chartData.completed_per_day_per_token_name,
		},
		{
			title: language === "en" ? "Responses per day per status" : "Nombre de réponse par jour et par status",
			data: hasFailed(chartData.response_per_day_per_status)
				? null
				: transformToWideFormat(
					chartData.response_per_day_per_status,
					"status",
					"total_responses"
				),
			failedData: chartData.response_per_day_per_status,
		},
		{
			title: language === "en" ? "Quota rejected per day per token" : "Nombre de réponse rejetées (Quota) par jour et par status",
			data: hasFailed(chartData.quota_rejected_per_day_per_token_name)
				? null
				: transformToWideFormat(
					chartData.quota_rejected_per_day_per_token_name,
					"token_name",
					"rejected_quota_responses"
				),
			failedData: chartData.quota_rejected_per_day_per_token_name,
		},
	];

	return (
		<div className="flex flex-col gap-8">
			{chartConfigs.map((chart, i) => {
				const isFailed = hasFailed(chart.failedData);
				const allKeys = chart.data && !isFailed
					? Object.keys(chart.data[0] || {}).filter((k) => k !== "day")
					: [];

				return (
					<div
						key={i}
						className="p-6 border shadow-sm bg-[var(--color-surface)]"
						style={{ 
							backgroundColor: 'var(--color-surface)',
							borderColor: 'color-mix(in srgb, var(--color-muted) 50%, transparent)'
						}}
					>
						<h3
							className="text-lg font-semibold mb-4"
							style={{ color: "var(--color-primary)" }}
						>
							{chart.title}
						</h3>

						<div style={{ width: "100%", height: 300 }}>
							{isFailed ? (
								<div className="w-full h-full flex flex-col items-center justify-center gap-3">
									<AlertCircle 
										size={32} 
										style={{ color: "var(--color-muted)" }}
									/>
									<p 
										className="text-sm text-center"
										style={{ color: "var(--color-muted)" }}
									>
										{language === "en" 
											? "Data temporarily unavailable" 
											: "Données temporairement indisponibles"}
									</p>
								</div>
							) : (
								<ResponsiveContainer>
									<LineChart data={chart.data}>
										<CartesianGrid
											stroke="color-mix(in srgb, var(--color-muted) 50%, transparent)"
											strokeDasharray="3 3"
										/>
										<XAxis dataKey="day" stroke="var(--color-text)" />
										<YAxis stroke="var(--color-text)" />
										<Tooltip />
										<Legend />
										{allKeys.map((key, index) => {
											const color = index === 0
												? "var(--color-primary)"
												: `hsl(${(index * 60) % 360}, 70%, 60%)`;

											return (
												<Line
													key={key}
													type="monotone"
													dataKey={key}
													stroke={color}
													strokeWidth={2}
													dot={false}
												/>
											);
										})}
									</LineChart>
								</ResponsiveContainer>
							)}
						</div>
					</div>
				);
			})}
		</div>
	);
}

export default MultiLineCharts;